import React, { useState, useCallback } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { useMutation, useQuery } from '@apollo/client';
import { GET_TODOS_AND_CATEGORIES, UPDATE_TODO, CREATE_TODO, MARK_DONE, MARK_NOT_DONE } from '../apis/graphqlQueries';
import { Box, CircularProgress, Typography, Paper, Chip, Stack, FormControlLabel, Switch, useTheme, useMediaQuery, Accordion, AccordionSummary, AccordionDetails, IconButton, ButtonGroup, Button } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import EditToDoDrawer from '../toDoList/editToDoDrawer';
import CompletionNoteDialog from '../toDoList/CompletionNoteDialog';
import './calendarStyles.css';

// Extend dayjs with plugins (still used for date handling elsewhere)
dayjs.extend(utc);
dayjs.extend(timezone);

// Use moment for react-big-calendar localizer
const localizer = momentLocalizer(moment);

// Generate consistent colors for categories
const generateColorForCategory = (category) => {
  if (!category) return '#3174ad';

  const colors = [
    '#3174ad', // blue
    '#7c3aed', // purple
    '#dc2626', // red
    '#ea580c', // orange
    '#16a34a', // green
    '#0891b2', // cyan
    '#db2777', // pink
    '#65a30d', // lime
  ];

  let hash = 0;
  for (let i = 0; i < category.length; i++) {
    hash = category.charCodeAt(i) + ((hash << 5) - hash);
  }

  return colors[Math.abs(hash) % colors.length];
};

function CalendarView({ user }) {
  const user_id = user.sub.split("|")[1];
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // State for drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedTodo, setSelectedTodo] = useState(null);
  const [isEdited, setIsEdited] = useState(false);

  // State for form fields
  const [title, setTitle] = useState("");
  const [listName, setListName] = useState("");
  const [description, setDescription] = useState("");
  const [dateTimeToDoToDo, setDateTimeToDoToDo] = useState("");
  const [notes, setNotes] = useState([]);
  const [percentDone, setPercentDone] = useState(0);
  const [isDone, setIsDone] = useState(false);

  // State for completion note dialog
  const [completionNoteDialogOpen, setCompletionNoteDialogOpen] = useState(false);
  const [todoToComplete, setTodoToComplete] = useState(null);

  // State for filtering
  const [showCompleted, setShowCompleted] = useState(false);

  // State for calendar navigation (mobile)
  const [currentDate, setCurrentDate] = useState(new Date());

  // GraphQL queries and mutations
  const { loading, error, data } = useQuery(GET_TODOS_AND_CATEGORIES, {
    variables: { user_id },
    notifyOnNetworkStatusChange: true,
  });

  const [updateTodo] = useMutation(UPDATE_TODO, {
    refetchQueries: [{ query: GET_TODOS_AND_CATEGORIES, variables: { user_id } }],
  });

  const [createTodo] = useMutation(CREATE_TODO, {
    refetchQueries: [{ query: GET_TODOS_AND_CATEGORIES, variables: { user_id } }],
  });

  const [markDone] = useMutation(MARK_DONE, {
    refetchQueries: [{ query: GET_TODOS_AND_CATEGORIES, variables: { user_id } }],
  });

  const [markNotDone] = useMutation(MARK_NOT_DONE, {
    refetchQueries: [{ query: GET_TODOS_AND_CATEGORIES, variables: { user_id } }],
  });

  // Transform todos into calendar events
  const events = React.useMemo(() => {
    if (!data?.getTodosAndCategories?.todos) return [];

    return data.getTodosAndCategories.todos
      .filter(todo => {
        // Filter out ALL admin contact todos (they are managed on the Contact page)
        if (todo.isAdminContactTodo === true) {
          return false;
        }
        // Show completed todos on their completion date, or incomplete todos on their scheduled date
        const hasDate = todo.isDone ? todo.dateDone_utc : todo.dateTimeToDoToDo;
        if (!hasDate) return false;
        if (!showCompleted && todo.isDone) return false;
        return true;
      })
      .map(todo => {
        // Use completion date for done todos, scheduled date for incomplete todos
        const dateToUse = todo.isDone ? todo.dateDone_utc : todo.dateTimeToDoToDo;
        const startDate = dayjs(dateToUse).toDate();
        // Default duration: 1 hour
        const endDate = dayjs(dateToUse).add(1, 'hour').toDate();

        return {
          id: todo.id,
          title: todo.listName + " - " + todo.title,
          start: startDate,
          end: endDate,
          resource: todo,
        };
      });
  }, [data, showCompleted]);

  // Get unscheduled todos
  const unscheduledTodos = React.useMemo(() => {
    if (!data?.getTodosAndCategories?.todos) return [];

    return data.getTodosAndCategories.todos
      .filter(todo => {
        // Filter out ALL admin contact todos (they are managed on the Contact page)
        if (todo.isAdminContactTodo === true) {
          return false;
        }
        // Exclude todos that have a date (either scheduled or completion date)
        const hasDate = todo.isDone ? todo.dateDone_utc : todo.dateTimeToDoToDo;
        if (hasDate) return false;
        if (!showCompleted && todo.isDone) return false;
        return true;
      });
  }, [data, showCompleted]);

  // Get categories
  const categories = React.useMemo(() => {
    if (!data?.getTodosAndCategories?.categories) return [];
    return data.getTodosAndCategories.categories;
  }, [data]);

  // Event style getter
  const eventStyleGetter = useCallback((event) => {
    const todo = event.resource;
    // Completed todos show in green, incomplete todos show in category color
    const backgroundColor = todo.isDone ? 'green' : generateColorForCategory(todo.listName);
    const opacity = 1; // Keep full opacity for better visibility

    return {
      style: {
        backgroundColor,
        opacity,
        borderRadius: '5px',
        border: 'none',
        display: 'block',
        textDecoration: todo.isDone ? 'line-through' : 'none',
      }
    };
  }, []);

  // Handle event selection (click on event)
  const handleSelectEvent = useCallback((event) => {
    const todo = event.resource;
    setSelectedTodo(todo);
    setIsEdited(true);
    setTitle(todo.title || "");
    setListName(todo.listName || "");
    setDescription(todo.description || "");
    setDateTimeToDoToDo(todo.dateTimeToDoToDo || "");
    setNotes(todo.notes || []);
    setPercentDone(todo.percentDone || 0);
    setIsDone(todo.isDone || false);
    setDrawerOpen(true);
  }, []);

  // Handle slot selection (click on empty slot)
  const handleSelectSlot = useCallback((slotInfo) => {
    setSelectedTodo(null);
    setIsEdited(false);
    setTitle("");
    setListName("");
    setDescription("");
    setDateTimeToDoToDo(dayjs(slotInfo.start).toISOString());
    setNotes([]);
    setPercentDone(0);
    setIsDone(false);
    setDrawerOpen(true);
  }, []);

  // Handle event drop (drag and drop)
  const handleEventDrop = useCallback(({ event, start, end }) => {
    const todo = event.resource;
    const newDate = dayjs(start).toISOString();

    updateTodo({
      variables: {
        id: todo.id,
        input: {
          title: todo.title,
          description: todo.description,
          listName: todo.listName,
          dateTimeToDoToDo: newDate,
          notes: todo.notes || [],
          isDone: todo.isDone,
          percentDone: todo.percentDone,
          order: todo.order,
          user_id: user_id,
        }
      }
    });
  }, [updateTodo, user_id]);

  // Handle event resize
  const handleEventResize = useCallback(({ event, start, end }) => {
    // For now, we'll just update the start time when resizing
    // The end time doesn't affect the todo itself
    handleEventDrop({ event, start, end });
  }, [handleEventDrop]);

  // Drawer handlers
  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setSelectedTodo(null);
    setIsDone(false);
  };

  const handleSaveTodo = () => {
    const todoInput = {
      title,
      description,
      listName,
      dateTimeToDoToDo,
      notes,
      isDone: selectedTodo?.isDone || false,
      percentDone,
      order: selectedTodo?.order || 0,
      user_id,
    };

    if (isEdited && selectedTodo) {
      // Update existing todo
      updateTodo({
        variables: {
          id: selectedTodo.id,
          input: todoInput,
        }
      });
    } else {
      // Create new todo
      createTodo({
        variables: {
          input: todoInput,
        }
      });
    }

    handleDrawerClose();
  };

  const handleDeleteTodo = () => {
    // For calendar view, we'll just close the drawer
    // Actual delete functionality can be added from the edit drawer
    handleDrawerClose();
  };

  // Handle marking done from drawer checkbox
  const handleDoneFromDrawer = useCallback((newIsDoneValue) => {
    setIsDone(newIsDoneValue);
    if (newIsDoneValue) {
      // Marking as done - show completion note dialog
      const todo = data ? data.getTodosAndCategories.todos.find((t) => t.id === selectedTodo?.id) : null;
      setTodoToComplete(todo);
      setCompletionNoteDialogOpen(true);
    } else {
      // Marking as not done
      markNotDone({
        variables: { user_id, id: selectedTodo?.id }
      });
    }
  }, [user_id, data, selectedTodo, markNotDone]);

  // Handle skipping completion note
  const handleCompletionNoteSkip = useCallback(() => {
    if (todoToComplete) {
      markDone({
        variables: { user_id, id: todoToComplete.id }
      }).then(() => {
        setCompletionNoteDialogOpen(false);
        setTodoToComplete(null);
      });
    }
  }, [todoToComplete, user_id, markDone]);

  // Handle adding completion note
  const handleCompletionNoteAdd = useCallback((noteText) => {
    if (todoToComplete && noteText.trim()) {
      // Add the completion note to the existing notes
      const cleanedNotes = todoToComplete.notes.map(({ __typename, ...rest }) => rest);
      const updatedNotes = [...cleanedNotes, {
        title: "Completion Note",
        note: noteText,
        isCreated: true
      }];

      // Update the todo with the new note
      const updatedTodo = {
        id: todoToComplete.id,
        input: {
          title: todoToComplete.title,
          description: todoToComplete.description,
          listName: todoToComplete.listName,
          dateTimeToDoToDo: todoToComplete.dateTimeToDoToDo,
          percentDone: todoToComplete.percentDone,
          notes: updatedNotes,
          user_id: user_id
        }
      };

      updateTodo({ variables: updatedTodo }).then(() => {
        // After updating the note, mark as done
        markDone({
          variables: { user_id, id: todoToComplete.id }
        }).then(() => {
          setCompletionNoteDialogOpen(false);
          setTodoToComplete(null);
        });
      });
    } else {
      // If note is empty, just skip
      handleCompletionNoteSkip();
    }
  }, [todoToComplete, user_id, updateTodo, markDone, handleCompletionNoteSkip]);

  // Handle unscheduled todo click
  const handleUnscheduledClick = (todo) => {
    setSelectedTodo(todo);
    setIsEdited(true);
    setTitle(todo.title || "");
    setListName(todo.listName || "");
    setDescription(todo.description || "");
    setDateTimeToDoToDo("");
    setNotes(todo.notes || []);
    setPercentDone(todo.percentDone || 0);
    setIsDone(todo.isDone || false);
    setDrawerOpen(true);
  };

  // Mobile navigation handlers
  const navigateToday = () => {
    setCurrentDate(new Date());
  };

  const navigatePrevious = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 1);
    setCurrentDate(newDate);
  };

  const navigateNext = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 1);
    setCurrentDate(newDate);
  };

  const formatDateHeader = (date) => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return "Today";
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return "Tomorrow";
    } else if (date.toDateString() === yesterday.toDateString()) {
      return "Yesterday";
    }
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
  if (error) return <Typography color="error">Error loading todos: {error.message}</Typography>;

  // Default view based on screen size
  const defaultView = isMobile ? 'day' : isTablet ? 'week' : 'week';
  const calendarViews = isMobile ? ['day', 'agenda'] : ['month', 'week', 'day', 'agenda'];

  return (
    <Box sx={{
      height: isMobile ? 'auto' : 'calc(100vh - 64px)',
      minHeight: isMobile ? '100vh' : 'auto',
      p: isMobile ? 1 : 2,
      paddingTop: isMobile ? '56px' : '64px',
      paddingBottom: isMobile ? 2 : 0
    }}>
      <Paper elevation={3} sx={{
        height: isMobile ? 'auto' : '100%',
        minHeight: isMobile ? 'calc(100vh - 80px)' : 'auto',
        p: isMobile ? 1 : 2,
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <Box sx={{
          mb: isMobile ? 1 : 2,
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'flex-start' : 'center',
          gap: isMobile ? 1 : 0
        }}>
          <Typography variant={isMobile ? "h6" : "h5"}>Todo Calendar</Typography>
          <FormControlLabel
            control={
              <Switch
                checked={showCompleted}
                onChange={(e) => setShowCompleted(e.target.checked)}
              />
            }
            label="Show Completed"
          />
        </Box>

        {/* Mobile Date Navigation */}
        {isMobile && (
          <Paper elevation={2} sx={{ mb: 1, p: 1 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <IconButton onClick={navigatePrevious} size="large">
                <ChevronLeftIcon />
              </IconButton>
              <Box display="flex" flexDirection="column" alignItems="center">
                <Typography variant="body1" fontWeight="bold">
                  {formatDateHeader(currentDate)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {currentDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </Typography>
              </Box>
              <Box>
                <IconButton onClick={navigateToday} size="medium" sx={{ mr: 0.5 }}>
                  <TodayIcon />
                </IconButton>
                <IconButton onClick={navigateNext} size="large">
                  <ChevronRightIcon />
                </IconButton>
              </Box>
            </Box>
          </Paper>
        )}

        {/* Unscheduled Todos */}
        {unscheduledTodos.length > 0 && (
          <Accordion
            sx={{ mb: isMobile ? 1 : 2 }}
            defaultExpanded={!isMobile}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="unscheduled-todos-content"
              id="unscheduled-todos-header"
            >
              <Typography variant="subtitle2">
                Unscheduled Todos ({unscheduledTodos.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {unscheduledTodos.map(todo => (
                  <Chip
                    key={todo.id}
                    label={todo.title}
                    onClick={() => handleUnscheduledClick(todo)}
                    size={isMobile ? "small" : "medium"}
                    sx={{
                      backgroundColor: generateColorForCategory(todo.listName),
                      color: 'white',
                      opacity: todo.isDone ? 0.5 : 1,
                      textDecoration: todo.isDone ? 'line-through' : 'none',
                    }}
                  />
                ))}
              </Stack>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Calendar */}
        <Box sx={{
          flex: 1,
          minHeight: isMobile ? '800px' : 0,
          height: isMobile ? 'auto' : '100%',
          '& .rbc-toolbar': {
            display: isMobile ? 'none' : 'flex'
          }
        }}>
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ height: '100%' }}
            onSelectEvent={handleSelectEvent}
            onSelectSlot={handleSelectSlot}
            onEventDrop={handleEventDrop}
            onEventResize={handleEventResize}
            eventPropGetter={eventStyleGetter}
            selectable
            resizable={!isMobile}
            views={calendarViews}
            defaultView={defaultView}
            popup
            date={isMobile ? currentDate : undefined}
            onNavigate={isMobile ? (newDate) => setCurrentDate(newDate) : undefined}
          />
        </Box>
      </Paper>

      {/* Edit Drawer */}
      <EditToDoDrawer
        drawerOpen={drawerOpen}
        closeDrawer={handleDrawerClose}
        title={title}
        onTitleChange={(e) => setTitle(e.target.value)}
        listName={listName}
        onListNameChange={(e) => setListName(e.target.value)}
        description={description}
        onDescriptionChange={(e) => setDescription(e.target.value)}
        dateTimeToDoToDo={dateTimeToDoToDo}
        onDateToDoToDoChange={(e) => setDateTimeToDoToDo(e.toISOString())}
        notes={notes}
        onNotesChange={(index, newNote, isDelete) => {
          if (isDelete) {
            const updatedNotes = [...notes];
            updatedNotes.splice(index, 1);
            setNotes(updatedNotes);
          } else {
            if (index > -1) {
              const updatedNotes = [...notes];
              updatedNotes[index] = {
                title: newNote.title,
                note: newNote.note,
                createdDate: newNote.createdDate,
                isUpdated: true
              };
              setNotes(updatedNotes);
            } else {
              setNotes([...notes, { title: newNote.title, note: newNote.note, isCreated: true }]);
            }
          }
        }}
        percentDone={percentDone}
        setPercentDone={setPercentDone}
        saveTodo={handleSaveTodo}
        isEdited={isEdited}
        listNames={categories.map(c => c.listName)}
        isDone={isDone}
        onIsDoneChange={handleDoneFromDrawer}
      />

      {/* Completion Note Dialog */}
      <CompletionNoteDialog
        open={completionNoteDialogOpen}
        onSkip={handleCompletionNoteSkip}
        onAddNote={handleCompletionNoteAdd}
      />
    </Box>
  );
}

export default CalendarView;
